/**
 * CSS inyectado DENTRO del Shadow DOM. Al vivir en el shadow root, ni filtra
 * estilos al host ni recibe los del host. `--dw-primary` se setea por JS desde el tema.
 */
export const WIDGET_CSS = `
:host {
  --dw-primary: #4f46e5;
  --dw-primary-contrast: #ffffff;
  --dw-bg: #ffffff;
  --dw-text: #1f2937;
  --dw-muted: #6b7280;
  --dw-in-bg: #f3f4f6;
  --dw-border: #e5e7eb;
  --dw-doc-pdf: #ef4444;
  --dw-doc-excel: #10b981;
  --dw-doc-csv: #2563eb;
  all: initial;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  line-height: 1.4;
}
*, *::before, *::after { box-sizing: border-box; }

.launcher {
  position: fixed;
  right: 20px;
  bottom: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: var(--dw-primary);
  color: var(--dw-primary-contrast);
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(0,0,0,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2147483000;
  transition: transform .15s ease;
}
.launcher:hover { transform: scale(1.05); }
.launcher svg { width: 26px; height: 26px; fill: currentColor; }

.panel {
  position: fixed;
  right: 20px;
  bottom: 88px;
  width: 360px;
  max-width: calc(100vw - 40px);
  height: 520px;
  max-height: calc(100vh - 120px);
  background: var(--dw-bg);
  color: var(--dw-text);
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.28);
  display: none;
  flex-direction: column;
  overflow: hidden;
  z-index: 2147483000;
}
.panel.open { display: flex; }

.header {
  background: var(--dw-primary);
  color: var(--dw-primary-contrast);
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.header .title { font-weight: 600; font-size: 15px; }
.header .status { font-size: 11px; opacity: .85; }
.header .close {
  background: transparent; border: none; color: inherit; cursor: pointer;
  font-size: 20px; line-height: 1; padding: 4px; border-radius: 6px;
}
.header .close:hover { background: rgba(255,255,255,.18); }
.header .actions { display: flex; align-items: center; gap: 2px; }
.header .mute {
  background: transparent; border: none; color: inherit; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  padding: 4px; border-radius: 6px;
}
.header .mute:hover { background: rgba(255,255,255,.18); }
.header .mute svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 2; }
.header .mute.muted { opacity: .7; }

.messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #fafafa;
}
.msg { max-width: 80%; padding: 8px 11px; border-radius: 12px; font-size: 14px; word-wrap: break-word; white-space: pre-wrap; }
.msg .who { display: block; font-size: 11px; color: var(--dw-muted); margin-bottom: 2px; }
.msg.out { align-self: flex-end; background: var(--dw-primary); color: var(--dw-primary-contrast); border-bottom-right-radius: 4px; }
.msg.in  { align-self: flex-start; background: var(--dw-in-bg); color: var(--dw-text); border-bottom-left-radius: 4px; }
.msg.out.error { background: #b91c1c; }
.msg .meta { display: block; font-size: 10px; opacity: .7; margin-top: 3px; }

/* Burbuja de imagen: el <img> ocupa el ancho de la burbuja con bordes redondeados. */
.msg.media { padding: 4px; }
.msg.media .img {
  display: block;
  max-width: 220px;
  width: 100%;
  height: auto;
  border-radius: 9px;
  cursor: pointer;
}
.msg.media .caption {
  display: block;
  padding: 5px 7px 2px;
  font-size: 14px;
  white-space: pre-wrap;
  word-wrap: break-word;
}
.msg.media .meta { padding: 0 7px 3px; }

/* Chip de documento dentro de la burbuja (PDF / Excel / CSV / genérico). */
.msg.media .doc {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 200px;
  max-width: 240px;
  padding: 8px 10px;
  border-radius: 9px;
  background: rgba(0,0,0,.05);
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}
.msg.out.media .doc { background: rgba(255,255,255,.16); }
.msg.media .doc:hover { background: rgba(0,0,0,.1); }
.msg.out.media .doc:hover { background: rgba(255,255,255,.24); }
.msg.media .doc-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 7px;
  background: var(--dw-bg);
  color: var(--dw-muted);
}
.msg.media .doc-icon svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.75; }
.msg.media .doc-icon.doc-pdf { color: var(--dw-doc-pdf); }
.msg.media .doc-icon.doc-excel { color: var(--dw-doc-excel); }
.msg.media .doc-icon.doc-csv { color: var(--dw-doc-csv); }
.msg.media .doc-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.msg.media .doc-dl { display: inline-flex; align-items: center; flex-shrink: 0; opacity: .7; }
.msg.media .doc-dl svg { width: 16px; height: 16px; fill: none; stroke: currentColor; stroke-width: 1.75; }

.empty { color: var(--dw-muted); font-size: 13px; text-align: center; margin: auto; padding: 0 16px; }

.notice {
  font-size: 12px;
  color: var(--dw-muted);
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  padding: 8px 14px;
  text-align: center;
}

.idle {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  background: #fafafa;
}
.idle[hidden] { display: none; }
.messages[hidden], .composer[hidden] { display: none; }
.idle-hint { color: var(--dw-muted); font-size: 14px; text-align: center; max-width: 240px; }
.start {
  border: none;
  background: var(--dw-primary);
  color: var(--dw-primary-contrast);
  border-radius: 10px;
  padding: 11px 20px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  box-shadow: 0 4px 14px rgba(0,0,0,0.18);
}
.start:hover { filter: brightness(1.05); }
.start:disabled { opacity: .6; cursor: not-allowed; }

.composer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid #e5e7eb;
  background: var(--dw-bg);
}
.composer .row { display: flex; gap: 8px; align-items: flex-end; }
.composer .row > textarea { flex: 1; }

/* Botón de adjuntar imagen (ícono clip). */
.composer .attach {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: var(--dw-bg);
  color: var(--dw-muted);
  cursor: pointer;
}
.composer .attach:hover { color: var(--dw-primary); border-color: var(--dw-primary); }
.composer .attach:disabled { opacity: .5; cursor: not-allowed; }
.composer .attach svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 2; }

/* Preview de la imagen adjunta antes de enviar. */
.attach-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
}
.attach-preview[hidden] { display: none; }
.attach-preview img { width: 44px; height: 44px; object-fit: cover; border-radius: 7px; }
.attach-preview img[hidden] { display: none; }
.attach-preview .doc-icon[hidden] { display: none; }
.attach-preview .doc-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 7px;
  background: var(--dw-bg);
  border: 1px solid var(--dw-border);
  color: var(--dw-muted);
}
.attach-preview .doc-icon svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.75; }
.attach-preview .doc-icon.doc-pdf { color: var(--dw-doc-pdf); }
.attach-preview .doc-icon.doc-excel { color: var(--dw-doc-excel); }
.attach-preview .doc-icon.doc-csv { color: var(--dw-doc-csv); }
.attach-preview .name { flex: 1; font-size: 12px; color: var(--dw-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.attach-preview .remove {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  color: var(--dw-muted);
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 6px;
}
.attach-preview .remove:hover { background: rgba(0,0,0,.08); color: var(--dw-text); }
.composer textarea {
  flex: 1;
  resize: none;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 9px 11px;
  font-family: inherit;
  font-size: 14px;
  max-height: 96px;
  outline: none;
  color: var(--dw-text);
}
.composer textarea:focus { border-color: var(--dw-primary); }
/* Botón de enviar: cuadrado con ícono de avión de papel. */
.composer .send {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: none;
  background: var(--dw-primary);
  color: var(--dw-primary-contrast);
  border-radius: 10px;
  cursor: pointer;
}
.composer .send:hover { filter: brightness(1.05); }
.composer .send svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 2; }
.composer .send:disabled { opacity: .5; cursor: not-allowed; }

/* Link discreto "Cambiar mis datos" bajo el botón de iniciar (modo público). */
.idle .link-btn {
  border: none;
  background: transparent;
  color: var(--dw-muted);
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
  padding: 2px 6px;
}
.idle .link-btn:hover { color: var(--dw-text); }
.idle .link-btn[hidden] { display: none; }

/* Formulario de pre-chat (modo público): card centrada en la vista idle. */
.prechat {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 18px;
  background: #fafafa;
  overflow-y: auto;
}
.prechat[hidden] { display: none; }
.prechat .card {
  background: var(--dw-bg);
  border: 1px solid var(--dw-border);
  border-radius: 14px;
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 4px 14px rgba(0,0,0,0.06);
}
.prechat .prechat-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--dw-text);
  margin: 0 0 2px;
}
.prechat .row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.prechat .phone-row { display: flex; gap: 8px; }
.prechat input, .prechat select {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 9px 11px;
  font-family: inherit;
  font-size: 14px;
  color: var(--dw-text);
  background: var(--dw-bg);
  outline: none;
  min-width: 0;
}
.prechat input::placeholder { color: #9ca3af; }
.prechat input:focus, .prechat select:focus { border-color: var(--dw-primary); }
.prechat input.invalid { border-color: #ef4444; }
.prechat select.prefix {
  flex: 0 0 auto;
  width: auto;
  padding-right: 6px;
  cursor: pointer;
}
.prechat .phone-row input { flex: 1; }
.prechat .prechat-error {
  font-size: 12px;
  color: #b91c1c;
  margin: 0;
}
.prechat .prechat-error[hidden] { display: none; }
.prechat .submit {
  border: none;
  background: var(--dw-primary);
  color: var(--dw-primary-contrast);
  border-radius: 10px;
  padding: 11px 16px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  margin-top: 2px;
}
.prechat .submit:hover { filter: brightness(1.05); }
.prechat .submit:disabled { opacity: .6; cursor: not-allowed; }

/* Footer permanente "Powered by dungo" (todos los modos y vistas). */
.powered {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 10px;
  border-top: 1px solid var(--dw-border);
  background: var(--dw-bg);
  color: #9ca3af;
  font-size: 11px;
  text-decoration: none;
  cursor: pointer;
}
.powered:hover { color: var(--dw-muted); }
.powered svg { height: 12px; width: auto; display: block; }
`;
